import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/useAuthStore';
import kycService from '../../services/kycService';
import snsWebSdk from '@sumsub/websdk';

const KYCVerification = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [showWebSDK, setShowWebSDK] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const webSdkContainerRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if this is a prompt after login
    const isPrompt = searchParams.get('prompt') === 'true';
    setShowPrompt(isPrompt);
    
    checkKYCStatus();
  }, [isAuthenticated, navigate, searchParams]);

  const checkKYCStatus = async () => {
    try {
      const response = await kycService.checkKYCStatus();
      // Normalize empty string to null for consistent handling
      const status = response.kyc_answer === '' ? null : response.kyc_answer;
      setKycStatus(status);
      
      if (status === 'GREEN') {
        toast.success('KYC verification completed!');
        navigate('/trade');
      }
    } catch (error) {
      console.error('Error checking KYC status:', error);
    }
  };

  const launchWebSDK = async (accessToken) => {
    try {
      const snsWebSdkInstance = snsWebSdk
        .init(accessToken, () => kycService.getKYCAccessToken().then(res => res.token))
        .withConf({
          lang: 'en',
          theme: 'light',
        })
        .on('onError', (error) => {
          console.error('WebSDK Error:', error);
          toast.error('Verification error. Please try again.');
        })
        .onMessage((type, payload) => {
          console.log('WebSDK Message:', type, payload);
          
          // Handle different message types
          if (type === 'idCheck.onApplicantSubmitted') {
            toast.success('Documents submitted successfully! We will review your application.');
            setTimeout(() => {
              checkKYCStatus();
              setShowWebSDK(false);
            }, 2000);
          } else if (type === 'idCheck.onApplicantResubmitted') {
            toast.info('Documents resubmitted for review.');
          }
        })
        .build();

      // Launch WebSDK in the container
      snsWebSdkInstance.launch('#sumsub-websdk-container');
    } catch (error) {
      console.error('Failed to launch WebSDK:', error);
      toast.error('Failed to initialize verification. Please try again.');
      setShowWebSDK(false);
      setIsLoading(false);
    }
  };

  const handleKYCVerification = async () => {
    setIsLoading(true);
    
    try {
      // Get KYC access token from backend via service
      const response = await kycService.getKYCAccessToken();
      const { token } = response;
      
      // Mark that prompt has been shown
      sessionStorage.setItem('kyc_prompt_shown', 'true');
      
      // Show the WebSDK container
      setShowWebSDK(true);
      setShowPrompt(false);
      setIsLoading(false);
      
      // Wait for container to render, then launch WebSDK
      setTimeout(() => {
        launchWebSDK(token);
      }, 100);
      
    } catch (error) {
      console.error('KYC verification error:', error);
      toast.error('Failed to start KYC verification. Please try again.');
      setIsLoading(false);
      setShowWebSDK(false);
    }
  };

  const handleSkipKYC = () => {
    // Mark that prompt has been shown so we don't show it again this session
    sessionStorage.setItem('kyc_prompt_shown', 'true');
    toast.success('You can verify your identity anytime from your profile');
    navigate('/');
  };

  const getStatusMessage = () => {
    switch (kycStatus) {
      case 'GREEN':
        return {
          title: 'Verification Complete',
          message: 'Your identity has been verified successfully.',
          color: 'text-green-600',
          bgColor: 'bg-green-50'
        };
      case 'RED':
        return {
          title: 'Verification Rejected',
          message: 'Your verification was rejected. Please contact support.',
          color: 'text-red-600',
          bgColor: 'bg-red-50'
        };
      default:
        return {
          title: 'Identity Verification Required',
          message: 'Please complete KYC verification to continue trading.',
          color: 'text-white',
          bgColor: 'bg-yellow-50'
        };
    }
  };

  const statusInfo = getStatusMessage();

  if (kycStatus === 'GREEN') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <svg className="h-6 w-6 text-green-600 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium  dark:text-white">
                {statusInfo.title}
              </h3>
              <p className="mt-1 text-sm text-white dark:text-white">
                {statusInfo.message}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/trade')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0E1696] hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Continue to Trading
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show simplified prompt after login for unverified users
  if (showPrompt && !showWebSDK) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg">
          <div className="bg-white dark:bg-gray-800 py-10 px-6 shadow-xl sm:rounded-2xl sm:px-12">
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900">
                <svg className="h-8 w-8 text-blue-600 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>

              {/* Title */}
              <h2 className="mt-6 text-2xl font-bold  dark:text-white">
                Verify Your Identity
              </h2>
              
              {/* Description */}
              <p className="mt-3 text-base text-white dark:text-white">
                Complete identity verification to unlock all features and ensure secure trading on our platform.
              </p>

              {/* Benefits */}
              <div className="mt-6 space-y-3 text-left">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-white dark:text-white">
                    Full access to trading features
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-white dark:text-white">
                    Enhanced account security
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-white dark:text-white">
                    Quick process - takes only 5 minutes
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 space-y-3">
                <button
                  onClick={handleKYCVerification}
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium !text-white bg-[#0E1696] hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </div>
                  ) : (
                    <>
                      Start Verification
                      <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleSkipKYC}
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-base font-medium text-white dark:text-white bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Skip for now
                </button>
              </div>

              {/* Security Note */}
              <p className="mt-6 text-xs text-white dark:text-white">
                🔒 Your data is encrypted and secure. We use industry-standard verification.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show WebSDK container if verification is started
  if (showWebSDK) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col sm:px-6 lg:px-8 py-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-extrabold  dark:text-white">
              Identity Verification
            </h2>
            <p className="mt-2 text-sm text-white dark:text-white">
              Please complete the verification process below
            </p>
            <button
              onClick={() => setShowWebSDK(false)}
              className="mt-4 text-sm text-blue-600 hover:text-blue-800 dark:text-white dark:hover:text-blue-300"
            >
              ← Back to verification info
            </button>
          </div>
          
          {/* SumSub WebSDK Container */}
          <div 
            id="sumsub-websdk-container" 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
            style={{ minHeight: '600px' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="mt-6 text-center text-3xl font-extrabold  dark:text-white">
            Identity Verification
          </h2>
          <p className="mt-2 text-center text-sm text-white dark:text-white">
            Complete verification to access all trading features
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            {/* Status Display */}
            <div className={`rounded-md p-4 ${statusInfo.bgColor} dark:bg-gray-700`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {kycStatus === 'RED' ? (
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${statusInfo.color} dark:text-white`}>
                    {statusInfo.title}
                  </h3>
                  <div className="mt-2 text-sm text-white dark:text-white">
                    <p>{statusInfo.message}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Steps */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium  dark:text-white">
                Verification Process
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600 dark:text-white">1</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium  dark:text-white">
                      Prepare Documents
                    </p>
                    <p className="text-sm text-white dark:text-white">
                      Have your ID, passport, or driver's license ready
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600 dark:text-white">2</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium  dark:text-white">
                      Complete Verification
                    </p>
                    <p className="text-sm text-white dark:text-white">
                      Follow the secure verification process
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600 dark:text-white">3</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium  dark:text-white">
                      Get Verified
                    </p>
                    <p className="text-sm text-white dark:text-white">
                      Access all trading features once approved
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div>
              <button
                onClick={handleKYCVerification}
                disabled={isLoading || kycStatus === 'RED'}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  isLoading || kycStatus === 'RED'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#0E1696] hover:bg-blue-700'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : kycStatus === 'RED' ? (
                  'Contact Support'
                ) : (
                  'Start Verification'
                )}
              </button>
            </div>

            {/* Additional Info */}
            <div className="text-center">
              <p className="text-xs text-white dark:text-white">
                Verification typically takes 5-10 minutes to complete
              </p>
              <p className="text-xs text-white dark:text-white mt-1">
                Your data is encrypted and secure
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCVerification;
